// Full-page visual editor for a workflow: the trigger plus a tree of steps on
// an xyflow canvas, with a properties panel for the selection.
// `/workflows/new?trigger=<type>[&data_type=<slug>]` creates; `/workflows/:id` edits.

import {
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type EdgeChange,
  type IsValidConnection,
  type NodeChange,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutGrid } from "lucide-mui";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  PROSPECT_ORDER_TRIGGER,
  SIGNAL_TRIGGER,
  api,
  getErrorMessage,
  type AgentContractOption,
  type AgentDefinition,
  type DataSourceOption,
  type ProspectList,
  type Workflow,
  type WorkflowDataType,
} from "../../../api/client.ts";
import { useAgentDataSources, useWorkflowDataTypes } from "../../../hooks/resources.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import NoSelectionPrompt from "../../../components/NoSelectionPrompt.tsx";
import PanelResizer, { RESIZER_WIDTH } from "../../../components/PanelResizer.tsx";
import { keys } from "../../../api/queryKeys.ts";
import { userHasGlobalScope } from "../../../auth/scopes.ts";
import { fieldDefaults } from "../../../components/fields/fieldSpecs.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useResizableWidth } from "../../../hooks/useResizableWidth.ts";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import {
  activeSelection,
  dataSourceNodesFor,
  isDataSourceChangeId,
  type DataSourceSelection,
} from "./dataSources.ts";
import {
  edgeId,
  emptyGraph,
  fingerprint,
  makeEdge,
  sized,
  toCreateBody,
  toGraph,
  toPayload,
  toUpdateBody,
  triggerDataOf,
  type DisplayLookups,
  type WorkflowPayload,
} from "./graph.ts";
import { AGENT_LEFT_GUTTER, NODE_HEIGHT, NODE_WIDTH, X_GAP, Y_GAP, layoutTree } from "./layout.ts";
import PropertiesPanel from "./PropertiesPanel.tsx";
import { nodeLabel } from "./StepNode.tsx";
import {
  KIND_META,
  MAX_STEP_DEPTH,
  PALETTE_KINDS,
  TRIGGER_ID,
  TRIGGER_META,
  agentInfo,
  defaultConditionFor,
  defaultDataFor,
  isConditionEdge,
  isDataSourceNode,
  isStepNode,
  isTriggerType,
  newStepId,
  type ActionKind,
  type ConditionEdge,
  type EditorEdge,
  type EditorNode,
  type StepNode,
  type TriggerType,
} from "./types.ts";
import {
  SubjectsContext,
  childSubjectOf,
  createsSubject,
  isFirstOnSubject,
  stepSubjects,
  subjectsOf,
} from "./subjects.ts";
import { hasErrors, validate, type Hint } from "./validation.ts";
import WorkflowCanvas from "./WorkflowCanvas.tsx";
import { BackBreadcrumbs } from "../../../components/PageHeader.tsx";

const FIT = { padding: 0.2, maxZoom: 1 };
const NO_DATA_TYPES: WorkflowDataType[] = [];
const NO_LISTS: ProspectList[] = [];
const NO_AGENTS: AgentDefinition[] = [];
const NO_CONTRACTS: AgentContractOption[] = [];
const NO_DATA_SOURCES: DataSourceOption[] = [];
const PANEL_WIDTH_PREFERENCE = "workflows.editor_panel_width";
const PANEL_DEFAULT_WIDTH = 340;
const PANEL_MIN_WIDTH = 280;
// The canvas keeps at least this much room however wide the panel is set.
const CANVAS_MIN_WIDTH = 320;

// Steps between the trigger and `id`, counting `id` (the trigger is 0).
function depthOf(id: string, edges: ConditionEdge[]): number {
  let depth = 0;
  let cur: string | undefined = id;
  const seen = new Set<string>();
  while (cur && cur !== TRIGGER_ID && !seen.has(cur)) {
    seen.add(cur);
    depth += 1;
    cur = edges.find((e) => e.target === cur)?.source;
  }
  return depth;
}

// Levels in the subtree rooted at `id`, counting `id`.
function heightOf(id: string, edges: ConditionEdge[], trail = new Set<string>()): number {
  if (trail.has(id)) return 0;
  const next = new Set(trail).add(id);
  const kids = edges.filter((e) => e.source === id).map((e) => heightOf(e.target, edges, next));
  return 1 + (kids.length ? Math.max(...kids) : 0);
}

function Editor() {
  const { id = "new" } = useParams<{ id: string }>();
  const isNew = id === "new";
  const [searchParams] = useSearchParams();
  const requestedTrigger = searchParams.get("trigger");
  // A new workflow's trigger comes from the URL; a saved one's from the row.
  const newTriggerType: TriggerType = isTriggerType(requestedTrigger)
    ? requestedTrigger
    : SIGNAL_TRIGGER;
  // A new signal workflow's data type, when the "New workflow" menu picked one.
  const requestedDataType = searchParams.get("data_type");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedTenantId, tenantPath } = useTenant();
  const { user } = useAuth();
  const canWrite = userHasGlobalScope(user, "signal_rules:write");
  const readOnly = !canWrite;

  const [nodes, setNodes, onNodesChange] = useNodesState<StepNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<ConditionEdge>([]);
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  // Fingerprint of the last loaded or saved state, for dirty tracking.
  const [baseline, setBaseline] = useState<string | null>(null);
  // Which workflow ("new" or an id) the canvas holds. The ref guards the load
  // effect so a refetch or setQueryData never clobbers edits in progress.
  const loadedRef = useRef<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  // Set by a successful create, until the URL catches up with the new id.
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Which data-source pill is open in the panel; pills are not xyflow-selectable.
  const [dataSourceSel, setDataSourceSel] = useState<DataSourceSelection | null>(null);
  // Properties panel width: saved per user, live while the divider is dragged,
  // and held back from the canvas's minimum when the window is narrow.
  const [splitEl, setSplitEl] = useState<HTMLDivElement | null>(null);
  const [splitWidth, setSplitWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!splitEl) return;
    const observer = new ResizeObserver((entries) => setSplitWidth(entries[0].contentRect.width));
    observer.observe(splitEl);
    return () => observer.disconnect();
  }, [splitEl]);
  const panelMaxWidth =
    splitWidth === null
      ? Infinity
      : Math.max(PANEL_MIN_WIDTH, splitWidth - RESIZER_WIDTH - CANVAS_MIN_WIDTH);
  const { width: panelWidth, resizer: panelResizer } = useResizableWidth({
    preference: PANEL_WIDTH_PREFERENCE,
    defaultWidth: PANEL_DEFAULT_WIDTH,
    min: PANEL_MIN_WIDTH,
    max: panelMaxWidth,
  });
  // The flow also holds the derived data-source nodes/edges: read past them.
  const { fitView, getNodes, getEdges } = useReactFlow<EditorNode, EditorEdge>();
  const getStepNodes = useCallback(() => getNodes().filter(isStepNode), [getNodes]);
  const getConditionEdges = useCallback(() => getEdges().filter(isConditionEdge), [getEdges]);

  const workflowQuery = useQuery({
    queryKey: keys.workflow.detail(id),
    queryFn: () => api.getWorkflow(id),
    enabled: !isNew,
  });
  const workflow = workflowQuery.data;
  const triggerType: TriggerType = isTriggerType(workflow?.trigger_type)
    ? workflow.trigger_type
    : newTriggerType;
  const isSignal = triggerType === SIGNAL_TRIGGER;
  const tenantId = workflow?.tenant_id ?? selectedTenantId ?? "";

  // Asked with the tenant: a data type's settings choices (which Notion pages)
  // and whether it can deliver anything to this tenant come back with it.
  const dataTypesQuery = useWorkflowDataTypes(tenantId, {
    enabled: isSignal && tenantId.length > 0,
  });
  const dataTypes = dataTypesQuery.data ?? NO_DATA_TYPES;
  const newDataType = dataTypes.find((t) => t.slug === requestedDataType) ?? dataTypes[0];

  const { data: stepTypes } = useQuery({
    queryKey: keys.workflowStepType.list(),
    queryFn: () => api.getWorkflowStepTypes(),
  });

  const { data: prospectListsData } = useQuery({
    queryKey: keys.prospectList.list(tenantId),
    queryFn: () => api.getProspectLists(tenantId),
    enabled: tenantId.length > 0,
  });
  const prospectLists = prospectListsData ?? NO_LISTS;

  const { data: agentsData } = useQuery({
    queryKey: keys.agentDefinition.list(tenantId),
    queryFn: () => api.getAgentDefinitions(tenantId),
    enabled: tenantId.length > 0,
  });
  const agents = agentsData ?? NO_AGENTS;
  const { data: contractsData } = useQuery({
    queryKey: keys.agentDefinition.contracts(),
    queryFn: () => api.getAgentContracts(),
  });
  const contracts = contractsData ?? NO_CONTRACTS;
  const { data: dataSourcesData } = useAgentDataSources(tenantId, {
    enabled: tenantId.length > 0,
  });
  const dataSources = dataSourcesData ?? NO_DATA_SOURCES;

  const lookups: DisplayLookups = useMemo(
    () => ({
      dataTypeLabel: (slug) => dataTypes.find((d) => d.slug === slug)?.label,
      listName: (listId) => prospectLists.find((l) => l.id === listId)?.name,
      agentInfo: (agentId) =>
        agentInfo(
          agents.find((a) => a.id === agentId),
          contracts,
        ),
    }),
    [dataTypes, prospectLists, agents, contracts],
  );

  const fitSoon = useCallback(() => {
    requestAnimationFrame(() => void fitView(FIT));
  }, [fitView]);

  // Load a workflow (or a blank one) onto the canvas. `keep` holds on to the
  // selection, trigger position and viewport, for re-applying after a save.
  const applyWorkflow = useCallback(
    (wf: Workflow | null, key: string, keep: boolean) => {
      const current = keep ? getStepNodes() : [];
      const currentEdges = keep ? getConditionEdges() : [];
      const triggerPosition = current.find((n) => n.id === TRIGGER_ID)?.position;
      const graph = wf
        ? toGraph(wf, lookups, { triggerPosition })
        : emptyGraph(
            newTriggerType,
            newDataType?.slug ?? "",
            lookups,
            fieldDefaults(newDataType?.config_fields ?? []),
          );
      const selectedNodes = new Set(current.filter((n) => n.selected).map((n) => n.id));
      const selectedEdges = new Set(currentEdges.filter((e) => e.selected).map((e) => e.id));
      const nextName = wf?.name ?? "";
      const nextActive = wf?.active ?? true;

      setNodes(graph.nodes.map((n) => (selectedNodes.has(n.id) ? { ...n, selected: true } : n)));
      setEdges(graph.edges.map((e) => (selectedEdges.has(e.id) ? { ...e, selected: true } : e)));
      setName(nextName);
      setActive(nextActive);
      setBaseline(fingerprint(toPayload(nextName, nextActive, graph.nodes, graph.edges)));
      loadedRef.current = key;
      setLoadedKey(key);
      if (!keep) fitSoon();
    },
    [getStepNodes, getConditionEdges, lookups, newDataType, newTriggerType, setNodes, setEdges, fitSoon],
  );

  useEffect(() => {
    if (loadedRef.current === id) return;
    if (isNew) {
      if (createdId) return; // the router is about to move to the created id
      // A signal workflow waits for the data types so it starts on a real one.
      if (newTriggerType !== SIGNAL_TRIGGER || dataTypesQuery.data) applyWorkflow(null, id, false);
    } else if (workflow && workflow.id === id) {
      applyWorkflow(workflow, id, false);
    }
  }, [id, isNew, createdId, workflow, newTriggerType, dataTypesQuery.data, applyWorkflow]);

  // Option queries may land after the graph: fill in display-only labels.
  useEffect(() => {
    setNodes((ns) => {
      let changed = false;
      const next = ns.map((n) => {
        const d = n.data;
        if (d.kind === "trigger" && d.trigger_type === SIGNAL_TRIGGER) {
          const label = lookups.dataTypeLabel(d.data_type);
          if (label && label !== d.data_type_label) {
            changed = true;
            return { ...n, data: { ...d, data_type_label: label } };
          }
        } else if (d.kind === "add_to_prospect_list" && d.collection_id) {
          const listName = lookups.listName(d.collection_id);
          if (listName && listName !== d.collection_name) {
            changed = true;
            return { ...n, data: { ...d, collection_name: listName } };
          }
        } else if (d.kind === "agent" && d.agent_id) {
          const info = lookups.agentInfo(d.agent_id);
          if (
            info.contract &&
            (info.contract !== d.contract ||
              info.agent_name !== d.agent_name ||
              info.contract_subject_kinds.length !== d.contract_subject_kinds.length ||
              info.output_fields !== d.output_fields ||
              // Or the Hive pill keeps "0 tools" when definitions land after the graph.
              info.hive_tool_count !== d.hive_tool_count ||
              info.allowed_data_sources.join() !== d.allowed_data_sources.join())
          ) {
            changed = true;
            return { ...n, data: { ...d, ...info } };
          }
        }
        return n;
      });
      return changed ? next : ns;
    });
  }, [lookups, loadedKey, setNodes]);

  // Right after a create the canvas already holds the new workflow while the
  // URL still says "new" (the router navigates in a transition); count that
  // as loaded so the canvas doesn't unmount for a render.
  const loaded = loadedKey === id || (isNew && createdId !== null && loadedKey === createdId);
  const payload = useMemo(() => toPayload(name, active, nodes, edges), [name, active, nodes, edges]);
  const isDirty = loaded && baseline !== null && fingerprint(payload) !== baseline;
  const hints = useMemo(
    () => validate(name, nodes, edges, dataSources, dataTypes, stepTypes),
    [name, nodes, edges, dataSources, dataTypes, stepTypes],
  );
  // What each step runs on: the trigger's subject, or the order a step made.
  const subjects = useMemo(() => subjectsOf(nodes, edges), [nodes, edges]);

  // What <ReactFlow> shows: the editor's state plus the derived data-source
  // pills. Those never enter `nodes`/`edges`, so saving, dirty tracking and
  // layout need no special cases; changes xyflow reports about them are dropped.
  const activeSel = useMemo(
    () => activeSelection(dataSourceSel, nodes, dataSources),
    [dataSourceSel, nodes, dataSources],
  );
  const derived = useMemo(
    () => dataSourceNodesFor(nodes, dataSources, activeSel),
    [nodes, dataSources, activeSel],
  );
  const flowNodes = useMemo<EditorNode[]>(() => [...nodes, ...derived.nodes], [nodes, derived]);
  const flowEdges = useMemo<EditorEdge[]>(() => [...edges, ...derived.edges], [edges, derived]);
  const onFlowNodesChange: OnNodesChange<EditorNode> = useCallback(
    (changes) => {
      const own = changes.filter(
        (c) => !isDataSourceChangeId(c.type === "add" ? c.item.id : c.id),
      ) as NodeChange<StepNode>[];
      if (own.length) onNodesChange(own);
    },
    [onNodesChange],
  );
  const onFlowEdgesChange: OnEdgesChange<EditorEdge> = useCallback(
    (changes) => {
      const own = changes.filter(
        (c) => !isDataSourceChangeId(c.type === "add" ? c.item.id : c.id),
      ) as EdgeChange<ConditionEdge>[];
      if (own.length) onEdgesChange(own);
    },
    [onEdgesChange],
  );

  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers only prompt when returnValue is set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const saveMutation = useMutation({
    mutationFn: ({ body, create }: { body: WorkflowPayload; create: boolean }) =>
      create ? api.createWorkflow(toCreateBody(body, tenantId)) : api.updateWorkflow(id, toUpdateBody(body)),
    onSuccess: (wf, { create }) => {
      queryClient.setQueryData(keys.workflow.detail(wf.id), wf);
      void queryClient.invalidateQueries({ queryKey: keys.workflow.all });
      // Lists show how many workflows feed them.
      void queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      // Signals show the workflow's name.
      if (!create) void queryClient.invalidateQueries({ queryKey: keys.signal.all });
      applyWorkflow(wf, wf.id, true);
      if (create) setCreatedId(wf.id);
      toast.success(create ? "Workflow created" : "Workflow saved");
      if (create) navigate(tenantPath(`/workflows/${wf.id}`), { replace: true });
    },
  });
  const blocked = hasErrors(hints);
  const canSave = canWrite && isDirty && !blocked;
  const firstError = hints.find((h) => h.level === "error");
  const saveReason = firstError ? firstError.message : !isDirty ? "No changes to save" : "";

  // Leaving for the list (Cancel / Back, the breadcrumb) with unsaved edits
  // asks first; a reload or closing the tab asks through `beforeunload` above.
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const listPath = tenantPath(`/workflows?trigger=${triggerType}`);
  const guardLink = (e: MouseEvent) => {
    // A new tab or window loses nothing here.
    if (!isDirty || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    setConfirmingLeave(true);
  };
  const cancel = () => {
    if (isDirty) setConfirmingLeave(true);
    else navigate(listPath);
  };
  const leaveDialog = (
    <ConfirmDialog
      open={confirmingLeave}
      title="Discard unsaved changes?"
      confirmLabel="Discard"
      cancelLabel="Keep editing"
      color="warning"
      onConfirm={() => {
        setConfirmingLeave(false);
        navigate(listPath);
      }}
      onClose={() => setConfirmingLeave(false)}
    >
      Your changes to this workflow have not been saved.
    </ConfirmDialog>
  );

  const tidy = useCallback(() => {
    setNodes((ns) => layoutTree(ns, getConditionEdges()));
    fitSoon();
  }, [setNodes, getConditionEdges, fitSoon]);

  const addStep = useCallback(
    (kind: ActionKind) => {
      const curNodes = getStepNodes();
      const curEdges = getConditionEdges();
      const selected = curNodes.find((n) => n.selected && KIND_META[n.data.kind].hasOutput);
      const parent = selected ?? curNodes.find((n) => n.id === TRIGGER_ID);
      if (!parent) return;
      const firstOnSubject = isFirstOnSubject(parent.id, parent.data);
      if (kind === "agent") {
        // One agent first on each subject; further agents chain under an agent.
        if (firstOnSubject) {
          const firstAgent = curEdges.some(
            (e) => e.source === parent.id && curNodes.find((n) => n.id === e.target)?.data.kind === "agent",
          );
          if (firstAgent) {
            toast.error("Only one agent runs here directly; select it to chain another agent under it.");
            return;
          }
        } else if (parent.data.kind !== "agent") {
          toast.error("An agent can only be chained under the trigger, another agent or a step that creates an order.");
          return;
        }
      }
      if (
        kind === "add_to_prospect_list" &&
        firstOnSubject &&
        childSubjectOf(parent, subjectsOf(curNodes, curEdges)) === PROSPECT_ORDER_TRIGGER
      ) {
        toast.error("On a prospect order, add the collection step after the agent.");
        return;
      }
      if (depthOf(parent.id, curEdges) >= MAX_STEP_DEPTH) {
        toast.error(`Steps can be chained at most ${MAX_STEP_DEPTH} deep.`);
        return;
      }

      const stepId = newStepId();
      const siblings = curEdges
        .filter((e) => e.source === parent.id)
        .map((e) => curNodes.find((n) => n.id === e.target))
        .filter((n): n is StepNode => !!n);
      // A new agent sibling leaves room for its data-source pills.
      const gutter = kind === "agent" ? AGENT_LEFT_GUTTER : 0;
      const x = siblings.length
        ? Math.max(...siblings.map((s) => s.position.x)) + NODE_WIDTH + X_GAP + gutter
        : parent.position.x;
      const node = sized({
        id: stepId,
        type: "step",
        position: { x, y: parent.position.y + NODE_HEIGHT + Y_GAP },
        data: defaultDataFor(kind),
        selected: true,
        deletable: true,
      });
      setNodes([...curNodes.map((n) => (n.selected ? { ...n, selected: false } : n)), node]);
      setEdges([
        ...curEdges.map((e) => (e.selected ? { ...e, selected: false } : e)),
        makeEdge(parent.id, stepId, defaultConditionFor(parent.data)),
      ]);
      fitSoon();
    },
    [getStepNodes, getConditionEdges, setNodes, setEdges, fitSoon],
  );

  const isValidConnection: IsValidConnection<EditorEdge> = useCallback(
    (c) => {
      if (readOnly || !c.source || !c.target || c.source === c.target) return false;
      const byId = new Map(getStepNodes().map((n) => [n.id, n]));
      const src = byId.get(c.source);
      const tgt = byId.get(c.target);
      if (!src || !tgt) return false;
      if (!KIND_META[src.data.kind].hasOutput || !KIND_META[tgt.data.kind].hasInput) return false;
      // An agent chains under the trigger, another agent or a step that makes an order.
      if (
        tgt.data.kind === "agent" &&
        src.id !== TRIGGER_ID &&
        src.data.kind !== "agent" &&
        !createsSubject(src.data)
      )
        return false;
      const curEdges = getConditionEdges();
      // The step must run on what the source hands down (a list step needs an
      // agent's finds on an order).
      const below = childSubjectOf(src, subjectsOf([...byId.values()], curEdges));
      if (tgt.data.kind !== "trigger" && !stepSubjects(tgt.data.kind, stepTypes).includes(below)) return false;
      if (
        tgt.data.kind === "add_to_prospect_list" &&
        below === PROSPECT_ORDER_TRIGGER &&
        isFirstOnSubject(src.id, src.data)
      )
        return false;
      // Keep it a tree: one parent per step.
      if (curEdges.some((e) => e.target === c.target)) return false;
      // No cycles: the target must not be an ancestor of the source.
      const seen = new Set<string>();
      let cur: string | undefined = c.source;
      while (cur && !seen.has(cur)) {
        if (cur === c.target) return false;
        seen.add(cur);
        cur = curEdges.find((e) => e.target === cur)?.source;
      }
      return depthOf(src.id, curEdges) + heightOf(tgt.id, curEdges) <= MAX_STEP_DEPTH;
    },
    [readOnly, getStepNodes, getConditionEdges, stepTypes],
  );

  const onConnect: OnConnect = useCallback(
    (c) => {
      const source = getStepNodes().find((n) => n.id === c.source);
      setEdges((es) =>
        addEdge<ConditionEdge>(
          {
            ...c,
            id: edgeId(c.source, c.target),
            targetHandle: c.targetHandle ?? "in",
            type: "condition",
            data: source ? defaultConditionFor(source.data) : {},
          },
          es,
        ),
      );
    },
    [getStepNodes, setEdges],
  );

  // A pill opens in the panel in place of any step/edge selection: by clicking
  // it, its hint, or its row in the agent step's panel.
  const openDataSource = useCallback(
    (sel: DataSourceSelection) => {
      setNodes((ns) => ns.map((x) => (x.selected ? { ...x, selected: false } : x)));
      setEdges((es) => es.map((e) => (e.selected ? { ...e, selected: false } : e)));
      setDataSourceSel(sel);
    },
    [setNodes, setEdges],
  );
  const onNodeClick = useCallback(
    (n: EditorNode) => {
      if (isDataSourceNode(n)) openDataSource({ agentNodeId: n.data.agent_node_id, key: n.data.source_key });
    },
    [openDataSource],
  );
  const onPaneClick = useCallback(() => setDataSourceSel(null), []);

  const focusHint = (h: Hint) => {
    if (h.nodeId && h.dataSourceKey) {
      openDataSource({ agentNodeId: h.nodeId, key: h.dataSourceKey });
    } else if (h.nodeId) {
      setEdges((es) => es.map((e) => (e.selected ? { ...e, selected: false } : e)));
      setNodes((ns) => ns.map((n) => ({ ...n, selected: n.id === h.nodeId })));
    } else if (h.edgeId) {
      setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n)));
      setEdges((es) => es.map((e) => ({ ...e, selected: e.id === h.edgeId })));
    }
  };

  const selectedNodes = nodes.filter((n) => n.selected);
  const selectedNode = selectedNodes.length === 1 ? selectedNodes[0] : undefined;
  const selectedEdge = selectedNode ? undefined : edges.find((e) => e.selected);
  // Selecting a step or edge (by click, or a hint) closes the pill's panel.
  useEffect(() => {
    if (selectedNode || selectedEdge) setDataSourceSel(null);
  }, [selectedNode, selectedEdge]);
  const showLegend =
    nodes.some((n) => n.data.kind === "agent") || edges.some((e) => !!e.data?.requires_approval);
  const addTarget =
    (selectedNode && KIND_META[selectedNode.data.kind].hasOutput ? selectedNode : undefined) ??
    nodes.find((n) => n.id === TRIGGER_ID);
  // What can go under the add target, in palette order: whatever runs on the
  // subject it hands down (a signal, or the order a step made). The backend's
  // step types are the source of truth, the local table the fallback.
  const addSubject = addTarget ? childSubjectOf(addTarget, subjects) : triggerType;
  const paletteKinds = PALETTE_KINDS.filter((k) => stepSubjects(k, stepTypes).includes(addSubject));

  // One chip per distinct message; clicking focuses the first offender.
  const visibleHints = hints.reduce<(Hint & { count: number })[]>((acc, h) => {
    const existing = acc.find((x) => x.message === h.message);
    if (existing) existing.count += 1;
    else acc.push({ ...h, count: 1 });
    return acc;
  }, []);
  const shownHints = visibleHints.slice(0, 5);

  // "Notion page", "prospect order": what the new workflow is about.
  const newTriggerData = triggerDataOf(nodes);
  const newWorkflowKind =
    newTriggerData?.trigger_type === SIGNAL_TRIGGER && newTriggerData.data_type_label
      ? newTriggerData.data_type_label
      : TRIGGER_META[triggerType].label.toLowerCase().replace(" trigger", "");
  const breadcrumbs = (
    <BackBreadcrumbs
      back={{ to: listPath, label: "Workflows", onClick: guardLink }}
      current={isNew ? `New ${newWorkflowKind} workflow` : (workflow?.name ?? "Workflow")}
      gutterBottom={false}
    />
  );

  let blocker: ReactNode = null;
  if (isNew && !canWrite) {
    blocker = <Alert severity="info">You don't have permission to create workflows.</Alert>;
  } else if (isNew && !selectedTenantId) {
    blocker = <NoSelectionPrompt kind="tenant" />;
  } else if (!isNew && workflowQuery.error) {
    blocker = <Alert severity="error">{getErrorMessage(workflowQuery.error)}</Alert>;
  } else if (isNew && isSignal && dataTypesQuery.error) {
    blocker = <Alert severity="error">{getErrorMessage(dataTypesQuery.error)}</Alert>;
  } else if (!loaded) {
    blocker = <CenteredSpinner />;
  }
  if (blocker) {
    return (
      <Stack spacing={2}>
        {breadcrumbs}
        <Box>{blocker}</Box>
      </Stack>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "calc(100vh - 48px)" }}>
      {breadcrumbs}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <TextField
          label="Workflow name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readOnly}
          sx={{ flexGrow: 1, minWidth: 260, maxWidth: 480 }}
        />
        <FormControlLabel
          control={
            <Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={readOnly} />
          }
          label="Active"
        />
        {isDirty && <Chip size="small" label="Unsaved changes" color="warning" variant="outlined" />}
        {readOnly && <Chip size="small" label="Read only" variant="outlined" />}
        <Box sx={{ flexGrow: 1 }} />
        {!readOnly && (
          <Button size="small" variant="outlined" startIcon={<LayoutGrid />} onClick={tidy}>
            Tidy up
          </Button>
        )}
        <Button size="small" onClick={cancel}>
          {readOnly ? "Back" : "Cancel"}
        </Button>
        {!readOnly && (
          <Tooltip title={canSave ? "" : saveReason} describeChild>
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={!canSave}
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate({ body: payload, create: isNew })}
              >
                {isNew ? "Create" : "Save"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", minHeight: 24 }}>
        {visibleHints.length === 0 ? (
          <Chip size="small" label="Workflow looks valid" color="success" variant="outlined" />
        ) : (
          shownHints.map((h, i) => (
            <Chip
              key={i}
              size="small"
              label={h.count > 1 ? `${h.message} (×${h.count})` : h.message}
              color={h.level}
              variant="outlined"
              onClick={h.nodeId || h.edgeId ? () => focusHint(h) : undefined}
            />
          ))
        )}
        {visibleHints.length > shownHints.length && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            +{visibleHints.length - shownHints.length} more
          </Typography>
        )}
      </Stack>

      <Box ref={setSplitEl} sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
        <SubjectsContext.Provider value={subjects}>
        <WorkflowCanvas
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onFlowNodesChange}
          onEdgesChange={onFlowEdgesChange}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onAddStep={addStep}
          paletteKinds={paletteKinds}
          addTargetLabel={addTarget ? nodeLabel(addTarget.data) : TRIGGER_META[triggerType].label}
          showLegend={showLegend}
          readOnly={readOnly}
        />
        </SubjectsContext.Provider>
        <PanelResizer {...panelResizer} label="Resize the properties panel" />
        <PropertiesPanel
          width={panelWidth}
          node={selectedNode}
          edge={selectedEdge}
          dataSource={activeSel}
          nodes={nodes}
          edges={edges}
          hints={hints}
          tenantId={tenantId}
          triggerType={triggerType}
          subjects={subjects}
          isNew={isNew}
          readOnly={readOnly}
          dataTypes={dataTypes}
          prospectLists={prospectLists}
          agents={agents}
          contracts={contracts}
          dataSources={dataSources}
          onOpenDataSource={openDataSource}
        />
      </Box>
      {leaveDialog}
    </Box>
  );
}

export default function WorkflowEditorPage() {
  return (
    <ReactFlowProvider>
      <Editor />
    </ReactFlowProvider>
  );
}
